import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AdCard, apiAdToCardProps } from './AdCard';
import { useGetAdsQuery } from '@nimbazaar/store';

interface CategoryAdsProps {
  title: string;
  categoryId: string;
  categorySlug: string;
}

function LoadingSkeleton() {
  return (
    <div className="flex gap-4 px-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="min-w-[160px] max-w-[180px] sm:min-w-[220px] sm:max-w-[240px] bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
        >
          <div className="h-44 bg-gray-200" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-5 bg-gray-200 rounded w-1/2" />
            <div className="h-3 bg-gray-200 rounded w-1/3" />
            <div className="flex justify-between pt-3 border-t border-gray-100">
              <div className="h-3 bg-gray-200 rounded w-16" />
              <div className="h-3 bg-gray-200 rounded w-12" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function CategoryAds({ title, categoryId, categorySlug }: CategoryAdsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useGetAdsQuery({ categoryId, limit: 6 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const ads = data?.data ?? [];

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, ads.length]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const cardWidth = 240;
      const gap = 16;
      const scrollAmount = cardWidth + gap;

      if (direction === 'left') {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    }
  };

  if (!isLoading && ads.length === 0) {
    return null;
  }

  return (
    <section className="bg-white pt-2 pb-4">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <Link
            to={`/category/${categorySlug}`}
            className="text-primary-500 hover:text-primary-600 text-sm font-medium flex items-center gap-1"
          >
            See more
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Cards Carousel */}
        <div className="relative group/carousel">
          {/* Left Arrow */}
          {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-secondary-100 shadow-lg rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover/carousel:opacity-60 hover:!opacity-100 transition-all duration-200 hover:scale-110 active:scale-95"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
          </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth px-1"
          >
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              ads.map((ad) => {
                const props = apiAdToCardProps(ad);
                return <AdCard key={ad.id} {...props} />;
              })
            )}
          </div>

          {/* Right Arrow */}
          {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-secondary-100 shadow-lg rounded-full flex items-center justify-center opacity-60 sm:opacity-0 sm:group-hover/carousel:opacity-60 hover:!opacity-100 transition-all duration-200 hover:scale-110 active:scale-95"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
          </button>
          )}
        </div>
      </div>
    </section>
  );
}
